import { useCallback, useMemo, useState } from 'react'

import * as ScreenOrientation from 'expo-screen-orientation'
import {
  useGetTabItemQuery,
  Note,
  useDeleteNoteStore,
  useFavoriteTabMutation,
  useUnfavoriteTabMutation
} from 'integration/resources/wallet'
import { useBreakpointValue } from 'native-base'
import { PanelsTypes } from 'organisms/TabPanels/constants'
import { FAVORITE_TYPE_ENUM } from 'src/screens/ContentScreen/ContentScreen.types'
import { useAuthAtomValue } from 'src/store/auth'

import { Classification } from './components/Classification'
import { Documents } from './components/Documents'
import { Ecosystem } from './components/Ecosystem'
import { Informations } from './components/Informations'
import { Notes } from './components/Notes'
import { Panels } from './components/Panels'
import { Regulatory } from './components/Regulatory'
import { Related } from './components/Related'
import { Summaries } from './components/Summaries'
import { UseTabScreen } from './TabScreen.types'
import { TabRelatedTypeProps } from '../TabRelatedScreen/TabRelatedScreen.types'

const changeScreenOrientation = (orientation: ScreenOrientation.OrientationLock) => {
  return ScreenOrientation.lockAsync(orientation)
}

export const useTabScreen: UseTabScreen = ({ navigation, route }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const authAtom = useAuthAtomValue()

  const { tabNumber } = route?.params

  const { data, isLoading } = useGetTabItemQuery(tabNumber)

  const tabDetail = data?.data.data

  const favoriteTabMutation = useFavoriteTabMutation(tabDetail?.identifier as string)

  const unfavoriteTabMutation = useUnfavoriteTabMutation(tabDetail?.identifier as string)

  const [isConfirmDeleteNoteOpen, setIsConfirmDeleteNoteOpen] = useState(false)

  const { mutate: deleteNoteMutation, isLoading: isLoadingNoteDelete } = useDeleteNoteStore()

  const handleToggleFavorite = (
    type: FAVORITE_TYPE_ENUM,
    mutationType: 'contact' | 'tab',
    id: string
  ) => {
    const toggle = [favoriteTabMutation, unfavoriteTabMutation]

    const mutation = type === FAVORITE_TYPE_ENUM.FAVORITE ? toggle[0] : toggle[1]

    return mutation.mutateAsync({ id, identifier: tabDetail?.identifier })
  }

  const isPesados = useMemo(
    () => authAtom?.user.business_code === 5,
    [authAtom?.user.business_code]
  )

  const handleGoBack = useCallback(() => navigation.goBack(), [navigation])

  const handleGoToTabDetails = useCallback(() => {
    if (tabDetail) {
      navigation.navigate('TabDetails', { tabNumber })
    }
  }, [navigation, tabDetail, tabNumber])

  const handleGoToTabLocation = useCallback(() => {
    if (tabNumber) navigation.navigate('TabLocation', { tabNumber })
  }, [navigation, tabNumber])

  const handleGoToTabContact = useCallback(
    () => navigation.navigate('TabContact', { tabNumber }),
    [navigation, tabNumber]
  )

  const handleGoToContactEdit = useCallback(() => {
    if (tabDetail)
      navigation.navigate('TabEdit', {
        tabDetail: { ...tabDetail, tab_number: tabNumber },
        tabNumber
      })
  }, [navigation, tabDetail, tabNumber])

  const handleGoToNoteDetails = useCallback(
    (id: string) => navigation.navigate('NoteDetails', { id, key: 'stores', tabNumber }),
    [navigation, tabNumber]
  )

  const handleGoToTabRelated = useCallback(
    (type: TabRelatedTypeProps) => navigation.navigate('TabRelated', { type, tabNumber }),
    [navigation, tabNumber]
  )

  const handleGoToNoteCreateDetails = useCallback(
    (tabNumber: string) => navigation.navigate('CreateNoteDetails', { tabNumber }),
    [navigation]
  )

  const [showOptions, setShowOptions] = useState(false)

  const handleOpenOptions = () => {
    setShowOptions(true)
  }

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)

  const handleConfirmDelete = (note: Note) => {
    deleteNoteMutation(
      { id: note!.id },
      {
        onSuccess: () => {
          setIsConfirmDeleteNoteOpen(false)
        },
        onError: () => {
          setIsConfirmDeleteNoteOpen(false)
        }
      }
    )
  }

  const handleOpenConfirmDelete = () => {
    setShowOptions(false)

    setIsConfirmDeleteOpen(true)
  }

  const handleDeleteContact = () => {
    // TO DO: DO SOMETHING
  }

  const handleGoToEditNote = useCallback(
    (note: Note) => {
      if (isMobile) {
        navigation.navigate('EditNoteDetails', {
          note,
          key: 'stores',
          tabNumber
        })

        return
      }

      navigation.navigate('TabEditNoteDetails', {
        note,
        key: 'stores',
        tabNumber
      })
    },
    [isMobile, navigation, tabNumber]
  )

  const handleGoToFullChartScreen = useCallback(
    (type: PanelsTypes) => {
      if (isMobile) {
        changeScreenOrientation(ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT).then(() => {
          navigation.navigate('Chart', { type, tabNumber })
        })

        return
      }

      navigation.navigate('Chart', { type, tabNumber })
    },
    [isMobile, navigation, tabNumber]
  )

  const [currentTab, setCurrentTab] = useState<number>(1)

  const isGoodsAndServices = useMemo(
    () => authAtom?.user.business_code === 3,
    [authAtom?.user.business_code]
  )

  const mainContent = {
    1: {
      component: Informations,
      name: 'Informações',
      show: true
    },
    2: {
      component: Summaries,
      name: 'Resumos',
      show: isGoodsAndServices && isMobile
    },
    3: {
      component: Regulatory,
      name: 'Regulatório',
      show: isGoodsAndServices && isMobile
    },
    4: {
      component: Notes,
      name: 'Notas',
      show: true
    },
    5: {
      component: Related,
      name: 'Relacionados',
      // @TODO: Tarefas foi temporariamente desabilitado devido a pedido do cliente
      // Oportunidades é escondido para isPesados, então não é necessário exibir o componente
      show: !isPesados
    },
    6: {
      component: Documents,
      name: 'Documentos',
      show: true
    },
    7: {
      component: Classification,
      name: 'Classificação',
      show: isGoodsAndServices && isMobile
    },
    8: {
      component: Panels,
      name: 'Painéis',
      show: true
    },
    9: {
      component: Ecosystem,
      name: 'Ecossistema',
      show: true
    }
  }

  return {
    isMobile,
    tabIsLoading: isLoading,
    handleGoToContactEdit,
    handleGoBack,
    handleGoToTabContact,
    favoriteIsLoading: favoriteTabMutation.isLoading || unfavoriteTabMutation.isLoading,
    showOptions,
    mainContent,
    mainContentCommonProps: {
      handleGoToNoteCreateDetails,
      handleGoToFullChartScreen,
      handleGoToNoteDetails,
      handleGoToTabLocation,
      handleGoToTabDetails,
      handleConfirmDelete,
      handleGoToTabContact,
      handleGoToContactEdit,
      handleGoToTabRelated,
      handleToggleFavorite,
      handleGoToEditNote,
      isLoadingNoteDelete,
      setIsConfirmDeleteNoteOpen,
      isConfirmDeleteNoteOpen,
      tabNumber,
      tabDetail,
      isPesados
    },
    handleOpenOptions,
    setShowOptions,
    handleOpenConfirmDelete,
    setIsConfirmDeleteOpen,
    handleDeleteContact,
    setCurrentTab,
    isConfirmDeleteOpen,
    tabIsFavorite: tabDetail?.is_favorite ?? false,
    currentTab,
    tabDetail: { ...tabDetail, tab_number: tabNumber },
    handleToggleFavorite
  }
}
